import React, { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Breadcrumb,
  Grid,
  HStack,
  Icon,
  Image,
  Input,
  Spinner,
  Tabs,
  Text,
} from "@chakra-ui/react";
import {
  MdCalendarToday,
  MdChevronRight,
  MdCollections,
  MdContentPaste,
  MdOutlineInventory,
  MdOutlineRocketLaunch,
  MdPhotoCamera,
  MdRocketLaunch,
} from "react-icons/md";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import {
  useDashboardActividad,
  useDashboardActividadEncargos,
  useDashboardActividadesMeta,
  usePatchDashboardActividad,
  useSetDashboardActividadCover,
} from "features/dashboard/actividades/hooks";
import { formatFechaRange } from "utils/formatters";
import { storage } from "lib/firebase";
import { sortLugaresForDisplay } from "features/dashboard/lugares/sort";

import { GeneralCard } from "./Resumen/GeneralCard";
import { HorarioCard } from "./Resumen/HorarioCard";
import { UbicacionCard } from "./Resumen/UbicacionCard";

import { PrevioLogisticaCard } from "./Previo/LogisticaCard";
import { PrevioEncargosCard } from "./Previo/EncargosCard";
import { PrevioMaterialesCard } from "./Previo/MaterialesCard";
import { PrevioFormacionCard } from "./Previo/FormacionCard";
import { PrevioHorarioCard } from "./Previo/HorarioCard";
import { PREVIO_USERS, NORMA_ITEMS } from "./Previo/PrevioMenu.constants";

import { PosteriorEvaluacionCard } from "./Posterior/EvaluacionCard";
import { PosteriorDesempenoCard } from "./Posterior/DesempenoCard";
import { PosteriorUsoCuidadosCard } from "./Posterior/UsoCuidadosCard";
import { PosteriorFormacionCard } from "./Posterior/FormacionCard";
import { PosteriorAsistenciaCard } from "./Posterior/AsistenciaCard";
import { PosteriorGastosCard } from "./Posterior/GastosCard";
import { NORMAS_CATALOG } from "./Posterior/PosteriorMenu.constants";

import FotosMenu from "./Fotos/FotosMenu";
import { MasonryItem, CardsMasonry } from "components/utils/CardsMasonry";
import { dashboardTabTriggerProps } from "layouts/Dashboard/tabStyles";
import { DEFAULT_ACTIVITY_COVER_URL } from "./actividadesList.helpers";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

function getFileExtension(file) {
  const base = (file?.name ?? "").split(".").pop()?.toLowerCase() ?? "";
  const cleaned = base.replace(/[^a-z0-9]/g, "");
  if (cleaned) return cleaned;
  if (file?.type === "image/jpeg") return "jpg";
  if (file?.type === "image/png") return "png";
  if (file?.type === "image/webp") return "webp";
  return "jpg";
}

const TAB_LIST = [
  { value: "actividad", label: "Actividad", TabIcon: MdRocketLaunch,    tabKey: "actividad" },
  { value: "previo",    label: "Previo",    TabIcon: MdContentPaste,    tabKey: "previo"    },
  { value: "posterior", label: "Posterior", TabIcon: MdOutlineInventory, tabKey: "posterior" },
  { value: "fotos",     label: "Fotos",     TabIcon: MdCollections,     tabKey: null        },
];

const ActividadesDetails = () => {
  const { id } = useParams();
  const [tab, setTab] = useState("actividad");
  const coverInputRef = useRef(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [coverUploadError, setCoverUploadError] = useState("");
  const [isCoverImageLoaded, setIsCoverImageLoaded] = useState(false);

  const q = useDashboardActividad(id, true);
  const actividad = q.data;

  const patch = usePatchDashboardActividad();
  const setCover = useSetDashboardActividadCover();
  const metaQ = useDashboardActividadesMeta(true);
  const desempenoQ = useDashboardActividadEncargos(id, !!id);

  const crumbTitle = actividad?.nombre?.trim() || "Actividad";
  const sidebarDate = useMemo(() => formatFechaRange(actividad), [actividad]);
  const shouldShowUsoCuidados = !!actividad?.club || !!actividad?.van;
  const shouldShowDesempeno = Array.isArray(desempenoQ.data) && desempenoQ.data.length > 0;

  const meta = metaQ.data ?? {};
  const categorias = meta.categorias ?? [];
  const tipos = meta.tipos ?? [];
  const lugares = useMemo(
    () => sortLugaresForDisplay(meta.lugares ?? []),
    [meta.lugares]
  );
  const nivelesDelSemestre = meta.nivelesDelSemestre ?? [];

  const maps = useMemo(() => ({
    categoriaById: new Map(categorias.map((c) => [c.id, c.nombre])),
    tipoById:      new Map(tipos.map((t) => [t.id, t.nombre])),
    lugarById:     new Map(lugares.map((l) => [l.id, l.nombre])),
    nivelById:     new Map(nivelesDelSemestre.map((n) => [n.nivelId, n.nivelNombre])),
  }), [categorias, tipos, lugares, nivelesDelSemestre]);

  const incompleteByTab = actividad?.incompleteByTab ?? null;
  const coverSrc = actividad?.coverUrl || DEFAULT_ACTIVITY_COVER_URL;

  React.useEffect(() => {
    let cancelled = false;
    const markLoaded = () => {
      if (!cancelled) setIsCoverImageLoaded(true);
    };

    setIsCoverImageLoaded(false);

    if (!coverSrc) {
      markLoaded();
      return () => {
        cancelled = true;
      };
    }

    const img = new window.Image();
    const timeoutId = window.setTimeout(markLoaded, 4000);

    img.onload = markLoaded;
    img.onerror = markLoaded;
    img.src = coverSrc;

    if (img.complete) markLoaded();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };
  }, [coverSrc]);

  const onSidebarCoverClick = () => { if (!isUploadingCover) coverInputRef.current?.click(); };

  const onSidebarCoverFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !id) return;

    if (!file.type?.startsWith("image/")) {
      setCoverUploadError("Solo se permiten archivos de imagen.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setCoverUploadError("El archivo supera el limite de 10MB.");
      return;
    }

    setCoverUploadError("");
    setIsUploadingCover(true);
    try {
      const imageId = crypto.randomUUID();
      const ext = getFileExtension(file);
      const storagePath = `actividades/${id}/cover/${imageId}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, file.type ? { contentType: file.type } : undefined);
      const downloadUrl = await getDownloadURL(storageRef);
      await setCover.mutateAsync({
        actividadId: id,
        payload: { imageId, storagePath, downloadUrl, contentType: file.type || null, sizeBytes: file.size },
      });
    } catch (e) {
      setCoverUploadError(String(e?.message || e || "No se pudo subir la portada."));
    } finally {
      setIsUploadingCover(false);
    }
  };

  const breadcrumb = (
    <Breadcrumb.Root pb="2">
      <Breadcrumb.List>
        <Breadcrumb.Item>
          <Breadcrumb.Link asChild>
            <Link to="/dashboard/club/actividades">
              <HStack gap="1.5"><MdOutlineRocketLaunch />Actividades</HStack>
            </Link>
          </Breadcrumb.Link>
        </Breadcrumb.Item>
        <Breadcrumb.Separator><MdChevronRight /></Breadcrumb.Separator>
        <Breadcrumb.Item>
          <Breadcrumb.CurrentLink>{crumbTitle}</Breadcrumb.CurrentLink>
        </Breadcrumb.Item>
      </Breadcrumb.List>
    </Breadcrumb.Root>
  );

  if (q.isLoading) {
    return (
      <Box>
        {breadcrumb}
        <Box
          bg="dashboard.bg.surface"
          rounded="card"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          p="10"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="3"
        >
          <Spinner size="sm" />
          <Text color="dashboard.fg.muted" fontSize="sm">Cargando actividad…</Text>
        </Box>
      </Box>
    );
  }

  if (q.isError) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" borderWidth="1px" borderColor="dashboard.border.muted" p="6">
          <Text color="dashboard.feedback.error" fontWeight="700">Error cargando actividad</Text>
          <Text as="pre" whiteSpace="pre-wrap" color="dashboard.feedback.error" mt="2" fontSize="sm">
            {String(q.error?.message || q.error)}
          </Text>
        </Box>
      </Box>
    );
  }

  if (!actividad) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" borderWidth="1px" borderColor="dashboard.border.muted" p="6">
          <Text color="dashboard.feedback.error" fontWeight="700">Actividad no encontrada en el periodo actual.</Text>
          <Text color="dashboard.fg.muted" mt="2" fontSize="sm">
            Revisa que el periodo seleccionado en el dashboard sea el correcto.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {breadcrumb}

      <Tabs.Root
        value={tab}
        onValueChange={(e) => setTab(e.value)}
        orientation="vertical"
        variant="subtle"
        colorPalette="red"
        w="full"
        display="block"
      >
        <Grid templateColumns={{ base: "1fr", md: "270px 1fr" }} gap="5" alignItems="start">

          {/* ── Sidebar ── */}
          <Box
            bg="dashboard.bg.surface"
            rounded="card"
            borderWidth="1px"
            borderColor="dashboard.border.muted"
            overflow="hidden"
            position={{ md: "sticky" }}
            top={{ md: "4" }}
          >
            {/* Cover image */}
            <Input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={onSidebarCoverFileChange}
              display="none"
            />
            <Box
              position="relative"
              overflow="hidden"
              bg="dashboard.bg.surfaceMuted"
              aspectRatio="4/3"
              cursor={isUploadingCover ? "progress" : "pointer"}
              role="group"
              onClick={onSidebarCoverClick}
            >
              <Image
                key={coverSrc}
                src={coverSrc}
                alt={crumbTitle}
                objectFit="cover"
                w="100%"
                h="100%"
                opacity={isUploadingCover || !isCoverImageLoaded ? 0.56 : 1}
                transition="transform 400ms ease, opacity 160ms ease"
                onLoad={() => setIsCoverImageLoaded(true)}
                onError={() => setIsCoverImageLoaded(true)}
                _groupHover={{ transform: isUploadingCover ? "none" : "scale(1.04)" }}
              />

              {/* Upload hover overlay */}
              <Box
                position="absolute"
                inset="0"
                bg="blackAlpha.600"
                opacity={isUploadingCover || !isCoverImageLoaded ? "1" : "0"}
                _groupHover={{ opacity: "1" }}
                transition="opacity 150ms ease"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap="1.5"
              >
                {isUploadingCover || !isCoverImageLoaded ? (
                  <>
                    <Spinner color="white" size="sm" />
                    <Text color="white" fontSize="xs" fontWeight="800">
                      {isUploadingCover ? "Subiendo portada..." : "Cargando portada..."}
                    </Text>
                  </>
                ) : (
                  <>
                    <Icon as={MdPhotoCamera} boxSize="5" color="white" />
                    <Text color="white" fontSize="xs" fontWeight="700">Cambiar portada</Text>
                  </>
                )}
              </Box>

              {/* Upload indicator */}
              <Box
                position="absolute"
                bottom="2.5"
                right="2.5"
                display="grid"
                placeItems="center"
                boxSize="7"
                rounded="full"
                bg="blackAlpha.500"
                color="white"
                pointerEvents="none"
                _groupHover={{ bg: "blackAlpha.700" }}
                transition="background 150ms ease"
              >
                {isUploadingCover ? <Spinner color="white" size="xs" /> : <Icon as={MdPhotoCamera} boxSize="3.5" />}
              </Box>

              {/* Status badge */}
              {!actividad.published && (
                <Badge
                  position="absolute"
                  top="3"
                  left="3"
                  colorPalette="gray"
                  variant="solid"
                  rounded="full"
                  px="2.5"
                  py="1"
                  fontSize="10px"
                  fontWeight="800"
                  letterSpacing="0.04em"
                >
                  Borrador
                </Badge>
              )}
            </Box>

            {/* Activity meta */}
            <Box px="4" pt="4" pb="3" borderBottomWidth="1px" borderColor="dashboard.border.muted">
              <Text
                fontFamily="display"
                fontWeight="700"
                fontSize="lg"
                lineHeight="1.2"
                color="dashboard.fg.default"
                lineClamp={2}
              >
                {crumbTitle}
              </Text>
              <HStack mt="2" gap="1.5" color="dashboard.fg.muted">
                <Icon as={MdCalendarToday} boxSize="3.5" flexShrink={0} />
                <Text fontSize="xs" fontWeight="600" lineClamp={1}>
                  {sidebarDate || "Fecha por definir"}
                </Text>
              </HStack>
              {coverUploadError && (
                <Text color="dashboard.feedback.error" fontSize="xs" mt="1.5" lineClamp={2}>
                  {coverUploadError}
                </Text>
              )}
            </Box>

            {/* Tab navigation */}
            <Tabs.List p="2" gap="0.5" flexDirection="column" bg="transparent" w="full">
              {TAB_LIST.map(({ value, label, TabIcon, tabKey }) => {
                const isSelected = tab === value;
                const hasDot = tabKey !== null && incompleteByTab?.[tabKey] === true;
                return (
                  <Tabs.Trigger key={value} value={value} w="full" {...dashboardTabTriggerProps}>
                    <HStack w="full" justify="space-between" gap="2">
                      <HStack gap="2.5">
                        <Box
                          display="grid"
                          placeItems="center"
                          boxSize="6"
                          rounded="md"
                          flexShrink={0}
                          bg={isSelected ? "red.100" : "transparent"}
                          _dark={{ bg: isSelected ? "red.800" : "transparent" }}
                          transition="background 150ms ease"
                        >
                          <TabIcon size="15px" />
                        </Box>
                        <Text>{label}</Text>
                      </HStack>
                      {hasDot && (
                        <Box w="7px" h="7px" rounded="full" bg="red.500" flexShrink={0} />
                      )}
                    </HStack>
                  </Tabs.Trigger>
                );
              })}
              <Tabs.Indicator rounded="md" />
            </Tabs.List>
          </Box>

          {/* ── Content ── */}
          <Box minW="0">
            <Tabs.Content p="0" value="actividad">
              <CardsMasonry>
                <MasonryItem>
                  <GeneralCard
                    actividad={actividad}
                    actividadId={id}
                    patch={patch}
                    categorias={categorias}
                    tipos={tipos}
                    nivelesDelSemestre={nivelesDelSemestre}
                    maps={maps}
                  />
                </MasonryItem>
                <HorarioCard actividad={actividad} actividadId={id} patch={patch} />
                <UbicacionCard actividad={actividad} actividadId={id} patch={patch} lugares={lugares} maps={maps} />
              </CardsMasonry>
            </Tabs.Content>

            <Tabs.Content p="0" value="previo">
              <CardsMasonry>
                <PrevioLogisticaCard actividad={actividad} actividadId={id} patch={patch} />
                <PrevioEncargosCard actividadId={id} users={PREVIO_USERS} />
                <PrevioMaterialesCard actividad={actividad} actividadId={id} patch={patch} />
                <PrevioFormacionCard actividad={actividad} actividadId={id} patch={patch} normaItems={NORMA_ITEMS} />
                <PrevioHorarioCard actividad={actividad} actividadId={id} patch={patch} />
              </CardsMasonry>
            </Tabs.Content>

            <Tabs.Content p="0" value="posterior">
              <CardsMasonry>
                <PosteriorEvaluacionCard actividad={actividad} actividadId={id} patch={patch} />
                {shouldShowUsoCuidados && (
                  <PosteriorUsoCuidadosCard actividad={actividad} actividadId={id} patch={patch} />
                )}
                {shouldShowDesempeno && <PosteriorDesempenoCard actividadId={id} />}
                <PosteriorGastosCard actividad={actividad} actividadId={id} patch={patch} />
                <PosteriorAsistenciaCard actividadId={id} />
                <PosteriorFormacionCard
                  actividad={actividad}
                  actividadId={id}
                  patch={patch}
                  normasCatalog={NORMAS_CATALOG}
                />
              </CardsMasonry>
            </Tabs.Content>

            <Tabs.Content p="0" value="fotos">
              <FotosMenu actividad={actividad} />
            </Tabs.Content>
          </Box>

        </Grid>
      </Tabs.Root>
    </Box>
  );
};

export default ActividadesDetails;
